import { useState } from "react";
import { BAY_ISLE, TERRACOTTA, SEMI_SWEET, PROPER_GREY, RULE, WHITE, FONT_DISPLAY, FONT_DISPLAY_HEAVY, FONT_SERIF, FONT_BODY } from "../theme";

function encode(data) {
  return Object.entries(data).map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join("&");
}

function InfoRow({ icon, label, value }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 18 }}>
      <div style={{
        width: 34, height: 34, borderRadius: 9, background: "rgba(195,135,98,0.1)",
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}>
        <i className={`ti ${icon}`} aria-hidden="true" style={{ fontSize: 16, color: TERRACOTTA }} />
      </div>
      <div>
        <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: PROPER_GREY, marginBottom: 3, fontFamily: FONT_BODY }}>{label}</p>
        <p style={{ fontSize: 13.5, color: BAY_ISLE, fontFamily: FONT_BODY, fontWeight: 600 }}>{value}</p>
      </div>
    </div>
  );
}

export default function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [brokerage, setBrokerage] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      setError("Please fill in your name, email, and message.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }
    setError(""); setLoading(true);
    try {
      await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({
          "form-name": "acadia-contact", "bot-field": "",
          full_name: name.trim(), email: email.trim(), brokerage: brokerage.trim(),
          subject: subject.trim(), message: message.trim(),
        }),
      });
      setSubmitted(true);
    } catch {
      setError("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 880, margin: "0 auto", padding: "3.5rem 20px 4rem" }}>
      <div style={{ textAlign: "center", marginBottom: "2.5rem" }}>
        <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.22em", textTransform: "uppercase", color: TERRACOTTA, marginBottom: 16, fontFamily: FONT_BODY }}>
          Get in touch
        </p>
        <h1 style={{ fontFamily: FONT_DISPLAY_HEAVY, fontSize: "clamp(26px, 5vw, 38px)", color: BAY_ISLE, lineHeight: 1.2, marginBottom: 14, fontWeight: 400 }}>
          Let's talk
        </h1>
        <p style={{ fontSize: 14, color: SEMI_SWEET, lineHeight: 1.7, maxWidth: 460, margin: "0 auto", fontFamily: FONT_BODY }}>
          Questions about a package, a partnership, or just want to say hi —
          we read every message personally.
        </p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 28 }}>

        {/* LEFT — info */}
        <div>
          <InfoRow icon="ti-mail" label="Email" value="inquiries@acadiamediamarketing.com" />
          <InfoRow icon="ti-brand-instagram" label="Instagram" value="@acadiamediamarketing" />
          <InfoRow icon="ti-map-pin" label="Based in" value="Hudson Valley, NY" />
          <InfoRow icon="ti-clock" label="Response time" value="Within 1–2 business days" />

          <div style={{ background: BAY_ISLE, borderRadius: 12, padding: "18px 18px", marginTop: 24 }}>
            <p style={{ fontFamily: FONT_SERIF, fontStyle: "italic", fontSize: 15, color: WHITE, lineHeight: 1.6 }}>
              "Not a tech question — a real estate question. That's exactly what we're here for."
            </p>
          </div>
        </div>

        {/* RIGHT — form */}
        <div style={{ background: WHITE, border: `1px solid ${RULE}`, borderRadius: 14, padding: "26px 24px" }}>
          {submitted ? (
            <div style={{ textAlign: "center", padding: "2rem 0" }}>
              <div style={{
                width: 52, height: 52, borderRadius: "50%", background: "rgba(195,135,98,0.1)",
                display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1rem",
              }}>
                <svg width="20" height="20" viewBox="0 0 22 22" fill="none">
                  <path d="M4 11l4.5 4.5L18 6" stroke={TERRACOTTA} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <p style={{ fontFamily: FONT_SERIF, fontStyle: "italic", fontSize: 22, color: BAY_ISLE, marginBottom: 8 }}>
                Message sent!
              </p>
              <p style={{ fontSize: 13, color: PROPER_GREY, lineHeight: 1.6, fontFamily: FONT_BODY }}>
                Thanks for reaching out — we'll get back to you within 1–2 business days.
              </p>
            </div>
          ) : (
            <form name="acadia-contact" method="POST" data-netlify="true" data-netlify-honeypot="bot-field" onSubmit={handleSubmit} noValidate>
              <input type="hidden" name="bot-field" />
              <input type="hidden" name="form-name" value="acadia-contact" />

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div>
                  <label>Full name *</label>
                  <input type="text" name="full_name" placeholder="Jane Smith" value={name} onChange={e => setName(e.target.value)} autoComplete="name" />
                </div>
                <div>
                  <label>Email address *</label>
                  <input type="email" name="email" placeholder="jane@brokerage.com" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" />
                </div>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label>Brokerage <span style={{ fontWeight: 400, color: PROPER_GREY }}>(optional)</span></label>
                <input type="text" name="brokerage" placeholder="Keller Williams, Compass, Howard Hanna..." value={brokerage} onChange={e => setBrokerage(e.target.value)} />
              </div>

              <div style={{ marginBottom: 12 }}>
                <label>Subject</label>
                <input type="text" name="subject" placeholder="What's this about?" value={subject} onChange={e => setSubject(e.target.value)} />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label>Message *</label>
                <textarea name="message" placeholder="Tell us what's on your mind..." value={message} onChange={e => setMessage(e.target.value)} rows={5} />
              </div>

              {error && (
                <p style={{ fontSize: 12, color: "#7a2a2a", background: "rgba(180,60,60,0.06)", border: "1px solid rgba(180,60,60,0.15)", borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontFamily: FONT_BODY }}>
                  {error}
                </p>
              )}

              <button type="submit" disabled={loading} style={{ width: "100%" }}>
                {loading ? "Sending..." : "Send message →"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
